import React from 'react';

import { useTranslate } from 'hooks/useTranslations';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { PaymentMethod, isServer } from 'utils/constants';
import { useFeature } from 'hooks/useFeature';
import { usePriceFormatter } from 'hooks/usePriceFormatter';
import { Maybe, useAfterPayConfigQuery } from '__generated__/graphql';

import { AfterpayInfo } from './AfterpayInfo';
import { KlarnaInfo } from './KlarnaInfo';

export const AfterpayOrKlarnaText: React.FC<{
  amount: Maybe<number> | undefined;
}> = props => {
  const t = useTranslate();
  const { currency } = useSiteConfig();
  const formatter = usePriceFormatter();
  const isAfterPayEnabled = useFeature(PaymentMethod.AFTERPAY);
  const isKlarnaEnabled = useFeature('KLARNA_ENABLED');

  const [afterPayConfig] = useAfterPayConfigQuery({
    pause: isServer || !isAfterPayEnabled,
  });

  if (
    !props.amount ||
    !isAfterPayEnabled ||
    !afterPayConfig.data?.afterPayConfig
  )
    return null;
  const afterpayText = t('afterpayText');

  const amountText = formatter(props.amount / 4);

  const textMatchWithComponent = {
    '{amount}': amountText,
    '{AfterPayInfo}': <AfterpayInfo />,
  };

  const afterpayWithAmountText = afterpayText.split(/({\w+})/).map(text => {
    return textMatchWithComponent[text] || text;
  });

  return (
    <div className="flex justify-center items-center flex-row! text-xs">
      {props.amount < afterPayConfig.data.afterPayConfig.min ||
      props.amount > afterPayConfig.data.afterPayConfig.max ? (
        <span data-test-id="afterpay-price-boundaries">
          {t<'afterPayBoundariesCartSummary'>('afterPayBoundariesCartSummary', {
            currency: currency.symbol,
            min: afterPayConfig.data.afterPayConfig.min,
            max: afterPayConfig.data.afterPayConfig.max,
          })}
        </span>
      ) : (
        <span
          data-test-id="afterpay-price-breakdown"
          className="flex justify-center items-center flex-row!"
        >
          {afterpayWithAmountText.map((segment, index) => (
            <React.Fragment key={index}>{segment}</React.Fragment>
          ))}
          {isKlarnaEnabled && (
            <React.Fragment>
              or <KlarnaInfo />
            </React.Fragment>
          )}
          <span className="sr-only">Afterpay</span>
        </span>
      )}
    </div>
  );
};
